import React from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { queryClient } from "../../../queries/client";
import { useAuthStore } from "../../../store/useAuthStore";
import { useSignOut } from "../../../queries/idServer";
import {
  useAuthorizationStore,
  useCompanyStore,
  usePermissionStore,
} from "../../../store";

export default function LogoutModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { clear } = useAuthStore();
  const { mutate: signOutMutation } = useSignOut();
  const { clearAuthorization } = useAuthorizationStore();
  const { clearPermission } = usePermissionStore();
  const { clearCompanyData } = useCompanyStore();

  async function logout() {
    try {
      localStorage.clear();
      sessionStorage.removeItem("accesstoken");
      sessionStorage.removeItem("currentUser");
      sessionStorage.removeItem("companyId");
      sessionStorage.removeItem("hissaUrl");
      queryClient.clear();
      clearAuthorization();
      clearPermission();
      clear();
      clearCompanyData();
      useCompanyStore.getState().clearCompanyData();
    } catch (err: any) {
      toast(err.response.data?.reason, { type: "error", autoClose: 2000 });
    }
  }
  return (
    <Transition.Root show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center sm:items-center sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-full"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-full"
            >
              <Dialog.Panel className="fixed bottom-0 w-full transform transition-all sm:static sm:mb-8 sm:w-full sm:max-w-lg">
                <div className="relative rounded-t-2xl bg-white px-4 pb-4 pt-5 shadow-xl sm:rounded-lg sm:px-6 sm:pt-6">
                  <div className="absolute left-0 right-0 -top-3 flex justify-center sm:hidden">
                    <div className="h-1 w-12 rounded-full bg-gray-300" />
                  </div>
                  <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => {
                        onClose();
                      }}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationTriangleIcon
                        className="h-6 w-6 text-red-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        Log out
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to logout your account?
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                      onClick={() => {
                        logout().then(() => {
                          signOutMutation({});
                          sessionStorage.clear();
                          clear();
                          //TODO: implement google auth
                          // clearGoogleAuth();
                          //TODO: logout redirection urls in platforms
                          onClose();
                          navigate("/login");
                        });
                      }}
                    >
                      Log out
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => {
                        onClose();
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
